'use client';

import { useState, useEffect } from 'react';
import Image, { type StaticImageData } from 'next/image';
import ScrollAnimate from '@/app/(marketing)/ScrollAnimate';
type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>;

import Esway1Icon from '@/assets/what_you_get_icons/Esway1';
import Esway2Icon from '@/assets/what_you_get_icons/Esway2';

import WygCloudLight from '@/assets/screenshots/wyg-cloud-light.webp';
import WygCloudDark from '@/assets/screenshots/wyg-cloud-dark.webp';
import WygAutopilotLight from '@/assets/screenshots/wyg-autopilot-light.webp';
import WygAutopilotDark from '@/assets/screenshots/wyg-autopilot-dark.webp';

import {
  GALLERIES,
  GALLERY_SIZES_LANDSCAPE,
  GALLERY_SIZES_PORTRAIT,
  isPortrait,
} from '@/components/whatYouGetGallery';

/** Card media sits in a fixed-height row with width:auto, so its rendered width
 *  follows the aspect ratio. Without a sizes hint next/image falls back to an
 *  x-descriptor srcset off the intrinsic width and the browser fetches the
 *  full-resolution variant (1920w/3840w) for a ~230px slot. */
const MEDIA_ROW_HEIGHT_PX = 144; // h-36
const cardFaceSizes = (img: StaticImageData) =>
  `${Math.ceil((MEDIA_ROW_HEIGHT_PX * img.width) / img.height)}px`;

interface CardData {
  id: number;
  slug: string;
  title: string;
  /** One-liner shown on the card; the full description lives in the modal. */
  blurb: string;
  description: string;
  /** Cards 1-2 show an illustration; cards 3-4 show real framed screenshots. */
  Icon?: IconType;
  media?: { light: StaticImageData; dark: StaticImageData };
  imageAlt: string;
  cardClasses: string;
}

const cards: CardData[] = [
  {
    id: 1,
    slug: 'sensors-in-every-zone',
    title: 'Sensors in Every Zone',
    blurb: 'A tailored sensor suite with the real-time insight to irrigate with confidence.',
    description: 'A tailored sensor suite that gives you the real-time insights you need to grow better, supporting confident decisions and precision irrigation.',
    Icon: Esway1Icon,
    imageAlt: 'Ensemble Way Icon 1',
    cardClasses:
      'from-[#257676]/10 to-[#257676]/5 border-[#257676]/20 dark:from-[#257676]/25 dark:to-transparent dark:border-[#257676]/30',
  },
  {
    id: 2,
    slug: 'intelligent-pump-and-valve-control',
    title: 'Intelligent Pump & Valve Control',
    blurb: 'A drop-in replacement for your controller. Run every zone independently using your existing valves.',
    description: 'Our smart controller is a drop-in replacement for existing controllers. Each zone is controlled independently using your existing valves and solenoids with an Ensemble controller.',
    Icon: Esway2Icon,
    imageAlt: 'Ensemble Way Icon 2',
    cardClasses:
      'from-slate-50 to-slate-100/50 border-slate-200 dark:from-[#1f2b27] dark:to-[#1a2522] dark:border-slate-800',
  },
  {
    id: 3,
    slug: 'powerful-cloud-platform',
    title: 'A Powerful Cloud Platform',
    blurb: 'Monitor, optimize, and control your whole operation from your phone or laptop.',
    description: 'Live field data at your fingertips. Monitor, optimize, and control every aspect of your operation from your phone or laptop, anytime, anywhere.',
    media: { light: WygCloudLight, dark: WygCloudDark },
    imageAlt: 'ESCloud platform showing live soil moisture data on a laptop and phone',
    cardClasses:
      'from-[#ff6b4a]/10 to-[#ff6b4a]/5 border-[#ff6b4a]/20 dark:from-[#ff6b4a]/20 dark:to-transparent dark:border-[#ff6b4a]/30',
  },
  {
    id: 4,
    slug: 'auto-pilot-for-your-irrigation',
    title: 'Auto-pilot for your Irrigation',
    blurb: 'Automation tuned to your crop, soil, and weather. Your knowledge stays in the loop.',
    description: 'Automation is tuned to your crop, soil, weather, and expertise. Override instantly whenever you want, your knowledge stays in the loop.',
    media: { light: WygAutopilotLight, dark: WygAutopilotDark },
    imageAlt: 'Auto-irrigation controls with per-zone conditions and toggles on a phone',
    cardClasses:
      'from-[#257676]/10 to-[#257676]/5 border-[#257676]/20 dark:from-[#257676]/25 dark:to-transparent dark:border-[#257676]/30',
  },
];

export default function EnsembleWaySection() {
  const [selectedCard, setSelectedCard] = useState<number | null>(null);

  const sectionId = 'what-you-get';

  const cardHash = (slug: string) => `#${sectionId}-${slug}`;

  const openModal = (cardId: number) => {
    setSelectedCard(cardId);

    const card = cards.find((c) => c.id === cardId);
    if (card && typeof window !== 'undefined') {
      const newHash = cardHash(card.slug);
      const { pathname, search } = window.location;
      window.history.replaceState(
        null,
        '',
        `${pathname}${search}${newHash}`,
      );
    }
  };

  const closeModal = () => {
    setSelectedCard(null);

    if (typeof window !== 'undefined') {
      const { pathname, search } = window.location;
      // Keep user on the section while clearing the specific card hash
      window.history.replaceState(
        null,
        '',
        `${pathname}${search}#${sectionId}`,
      );
    }
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (!selectedCard) return;

    document.body.style.overflow = 'hidden';
    document.body.dataset.modalOpen = 'true';

    return () => {
      document.body.style.overflow = '';
      delete document.body.dataset.modalOpen;
    };
  }, [selectedCard]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedCard) {
        closeModal();
      }
    };

    if (selectedCard) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [selectedCard]);

  // Open modal based on URL hash on initial load and when hash changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncFromHash = () => {
      const rawHash = window.location.hash.replace('#', '');
      if (!rawHash) {
        setSelectedCard(null);
        return;
      }

      // Expect hashes like "what-you-get-slug"
      const prefix = `${sectionId}-`;
      if (!rawHash.startsWith(prefix)) return;

      const slug = rawHash.slice(prefix.length);
      const card = cards.find((c) => c.slug === slug);
      if (card) {
        setSelectedCard(card.id);
      }
    };

    syncFromHash();

    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, []);

  const selectedCardData = selectedCard ? cards.find(card => card.id === selectedCard) : null;

  return (
    <>
      <section
        id={sectionId}
        className="bg-white py-12 dark:bg-transparent md:py-20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimate>
            <div className="mb-8 text-center md:mb-12">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#257676] dark:text-[#4dbdbd]">
                Irrigation as a Service
              </p>
              <h2 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl dark:text-slate-100">
                What you get
              </h2>
              <p className="mt-3 text-lg font-light text-slate-500 max-w-2xl mx-auto dark:text-slate-400">
                We install the devices and keep them running smoothly
              </p>
            </div>
          </ScrollAnimate>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {cards.map((card, index) => (
              <ScrollAnimate key={card.id} delay={(index + 1) * 100} className="h-full">
                <div
                  onClick={() => openModal(card.id)}
                  className={`flex h-full cursor-pointer flex-col rounded-2xl border bg-gradient-to-br p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:hover:shadow-none dark:hover:ring-1 dark:hover:ring-white/20 md:p-6 ${card.cardClasses}`}
                >
                  <div className="mb-4 flex h-28 items-center justify-center md:mb-5 md:h-36">
                    {card.media ? (
                      <>
                        {/* globals.css sets img height:auto outside CSS layers, so size inline */}
                        <Image
                          src={card.media.dark}
                          alt={card.imageAlt}
                          sizes={cardFaceSizes(card.media.dark)}
                          className="hidden object-contain dark:block"
                          style={{ height: '100%', width: 'auto' }}
                        />
                        <Image
                          src={card.media.light}
                          alt={card.imageAlt}
                          sizes={cardFaceSizes(card.media.light)}
                          className="object-contain drop-shadow-md dark:hidden"
                          style={{ height: '100%', width: 'auto' }}
                        />
                      </>
                    ) : card.Icon ? (
                      <card.Icon
                        className="h-full w-auto"
                        role="img"
                        aria-label={card.imageAlt}
                      />
                    ) : null}
                  </div>
                  <h3 className="text-lg font-semibold tracking-tight text-slate-900 mb-2 dark:text-slate-100">
                    {card.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                    {card.blurb}
                  </p>
                </div>
              </ScrollAnimate>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedCardData && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity duration-200"
          onClick={closeModal}
        >
          <div
            className="relative max-w-2xl w-full overflow-hidden bg-white rounded-3xl shadow-2xl transform transition-all duration-200 scale-100 dark:bg-[#1a2522] dark:ring-1 dark:ring-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal body: fixed header row (title + close) above a scroll
                area, so the scrollbar starts below the header and stays
                inside the clipped rounded shell */}
            <div className={`flex max-h-[90vh] flex-col bg-gradient-to-br border rounded-3xl ${selectedCardData.cardClasses}`}>
              <div className="flex shrink-0 items-start justify-between gap-4 px-8 pt-6 pb-4 md:px-12 md:pt-8">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {selectedCardData.title}
                </h3>
                <button
                  onClick={closeModal}
                  className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors duration-200 dark:bg-white/10 dark:hover:bg-white/20"
                  aria-label="Close modal"
                >
                  <svg
                    className="w-6 h-6 text-slate-600 dark:text-slate-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              {/* pt-1 keeps the first image's 1px ring from being clipped by the scroll overflow */}
              <div className="min-h-0 overflow-y-auto px-8 pt-1 pb-8 md:px-12 md:pb-12">
              <div className="mb-6 space-y-4">
                {GALLERIES[selectedCardData.slug].map((item) => {
                  const portrait = isPortrait(item.image);
                  const sizes = portrait
                    ? GALLERY_SIZES_PORTRAIT
                    : GALLERY_SIZES_LANDSCAPE;
                  return (
                  <figure
                    key={item.caption}
                    className={portrait ? 'mx-auto w-3/5 sm:w-2/5' : ''}
                  >
                    {item.imageDark ? (
                      <>
                        <Image
                          src={item.imageDark}
                          alt={item.caption}
                          sizes={sizes}
                          className="hidden w-full rounded-xl ring-1 ring-white/10 dark:block"
                        />
                        <Image
                          src={item.image}
                          alt={item.caption}
                          sizes={sizes}
                          className="w-full rounded-xl ring-1 ring-slate-900/10 dark:hidden"
                        />
                      </>
                    ) : (
                      <Image
                        src={item.image}
                        alt={item.caption}
                        sizes={sizes}
                        className="w-full rounded-xl object-cover"
                      />
                    )}
                    <figcaption className="mt-2 mb-2 text-xs text-slate-500 dark:text-slate-400">
                      {item.caption}
                    </figcaption>
                  </figure>
                  );
                })}
              </div>
              <p className="text-slate-600 leading-relaxed dark:text-slate-300">
                {selectedCardData.description}
              </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
